<template>
    <div>
        <!-- Filter Section -->
        <div class="card mb-3">
            <div class="card-header">
                <h5 class="mb-0">{{ trans('Filter') }}</h5>
            </div>
            <div class="card-body">
                <div class="row">
                    <!-- Start Date -->
                    <div class="col-md-3">
                        <label for="date_start">{{ trans('Start Date') }}</label>
                        <vue-flatpickr
                            :modelValue="filter.date_start"
                            :config="{
                                allowInput: true,
                                altFormat: 'd/m/Y',
                                altInput: true,
                                dateFormat: 'Y-m-d',
                            }"
                            :placeholder="trans('Select start date')"
                            name="date_start"
                            class="form-control bg-white"
                            autocomplete="off"
                            @update:modelValue="onDateStartChange"
                        >
                        </vue-flatpickr>
                    </div>

                    <!-- End Date -->
                    <div class="col-md-3">
                        <label for="date_end">{{ trans('End Date') }}</label>
                        <vue-flatpickr
                            :modelValue="filter.date_end"
                            :config="{
                                allowInput: true,
                                altFormat: 'd/m/Y',
                                altInput: true,
                                dateFormat: 'Y-m-d',
                            }"
                            :placeholder="trans('Select end date')"
                            name="date_end"
                            class="form-control bg-white"
                            autocomplete="off"
                            @update:modelValue="onDateEndChange"
                        >
                        </vue-flatpickr>
                    </div>

                    <!-- Departments -->
                    <div class="col-md-4">
                        <label>{{ trans('Departments') }}</label>
                        <div class="departments-container">
                            <template v-if="loadingDepartments">
                                <div class="text-center py-2">
                                    <i class="icofont-spinner"></i>
                                    {{ trans('Loading...') }}
                                </div>
                            </template>
                            <template v-else-if="departments.length === 0">
                                <div class="text-muted text-center py-2">
                                    {{ trans('No departments found') }}
                                </div>
                            </template>
                            <template v-else>
                                <div class="form-check mb-1">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        id="select-all-departments"
                                        :checked="areAllDepartmentsSelected"
                                        @change="toggleAllDepartments"
                                    >
                                    <label class="form-check-label font-weight-bold" for="select-all-departments">
                                        {{ trans('Select All') }}
                                    </label>
                                </div>
                                <hr class="my-2">
                                <div class="departments-list">
                                    <div
                                        v-for="department in departments"
                                        :key="department.id"
                                        class="form-check mb-1"
                                    >
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            :id="'dept-' + department.id"
                                            :value="department.id"
                                            v-model="filter.selected_departments"
                                        >
                                        <label class="form-check-label" :for="'dept-' + department.id">
                                            {{ department.text }}
                                        </label>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <small class="text-muted">
                            {{ selectedDepartmentsText }}
                        </small>
                    </div>

                    <!-- Filter Button -->
                    <div class="col-md-2 d-flex align-items-end">
                        <button
                            @click="filterTasks"
                            :disabled="loadingTasks || !filter.date_start || !filter.date_end"
                            class="btn btn-primary w-100"
                        >
                            <i class="icofont-filter" v-if="!loadingTasks"></i>
                            <i class="icofont-spinner" v-else></i>
                            {{ trans('Filter') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Validation Errors -->
        <div class="card mb-3 border-danger" v-if="validationErrors && Object.keys(validationErrors).length > 0">
            <div class="card-header bg-danger text-white">
                <h5 class="mb-0">
                    <i class="icofont-info-circle"></i>
                    {{ trans('Data Configuration Required') }}
                </h5>
            </div>
            <div class="card-body">
                <p class="mb-3 text-muted">
                    {{ trans('Please configure the following data before generating the SAP export:') }}
                </p>
                
                <!-- Tasks without internal_id -->
                <div v-if="validationErrors.tasks_without_internal_id" class="mb-4">
                    <h6 class="text-danger">
                        <i class="icofont-tasks"></i>
                        {{ trans('Tasks need Internal ID (Task or Project)') }}
                    </h6>
                    <ul class="list-unstyled">
                        <li v-for="task in validationErrors.tasks_without_internal_id" :key="task.task_name + '|' + task.project_name" class="mb-1">
                            <strong>{{ task.task_name }}</strong>
                            <span v-if="task.project_name" class="text-muted"> ({{ task.project_name }})</span>
                        </li>
                    </ul>
                </div>
                
                <!-- Persons without job_id -->
                <div v-if="validationErrors.persons_without_job_id" class="mb-4">
                    <h6 class="text-danger">
                        <i class="icofont-users"></i>
                        {{ trans('Persons need Job') }}
                    </h6>
                    <ul class="list-unstyled">
                        <li v-for="person in validationErrors.persons_without_job_id" :key="person.person_name" class="mb-1">
                            <strong>{{ person.person_name }}</strong>
                        </li>
                    </ul>
                </div>
                
                <!-- Persons without badge_number -->
                <div v-if="validationErrors.persons_without_badge_number" class="mb-4">
                    <h6 class="text-danger">
                        <i class="icofont-id-card"></i>
                        {{ trans('Persons need Badge Number') }}
                    </h6>
                    <ul class="list-unstyled">
                        <li v-for="person in validationErrors.persons_without_badge_number" :key="person.person_name" class="mb-1">
                            <strong>{{ person.person_name }}</strong>
                        </li>
                    </ul>
                </div>
                
                <!-- Persons without internal_number -->
                <div v-if="validationErrors.persons_without_internal_number" class="mb-4">
                    <h6 class="text-danger">
                        <i class="icofont-numbered"></i>
                        {{ trans('Persons need Internal Number') }}
                    </h6>
                    <ul class="list-unstyled">
                        <li v-for="person in validationErrors.persons_without_internal_number" :key="person.person_name" class="mb-1">
                            <strong>{{ person.person_name }}</strong>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Tasks List -->
        <div class="card" v-if="tasks.length > 0 || hasFiltered">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">{{ trans('Tasks') }}</h5>
                <div>
                    <!-- Select All / Clear All -->
                    <button
                        @click="selectAll"
                        class="btn btn-sm btn-outline-primary mr-2"
                        style="min-width: 100px;"
                        :disabled="tasks.length === 0"
                    >
                        {{ trans('Select All') }}
                    </button>
                    <button
                        @click="clearAll"
                        class="btn btn-sm btn-outline-secondary mr-2"
                        style="min-width: 100px;"
                        :disabled="selectedTasks.length === 0"
                    >
                        {{ trans('Clear All') }}
                    </button>
                    <!-- Export Button -->
                    <button
                        @click="exportToExcel"
                        :disabled="selectedTasks.length === 0 || loadingExport"
                        class="btn btn-sm btn-primary"
                        style="min-width: 100px;"
                    >
                        <i class="icofont-download" v-if="!loadingExport" style="font-size: 14px;"></i>
                        <i class="icofont-spinner" v-else style="font-size: 14px;"></i>
                        {{ trans('Create XLS') }}
                    </button>
                </div>
            </div>
            <div class="card-body">
                <template v-if="loadingTasks">
                    <div class="text-center py-4">
                        <i class="icofont-spinner"></i>
                        {{ trans('Loading tasks...') }}
                    </div>
                </template>
                <template v-else-if="tasks.length === 0">
                    <div class="text-center py-4 text-muted">
                        {{ trans('No tasks found for the selected criteria.') }}
                    </div>
                </template>
                <template v-else>
                    <div class="task-list">
                        <div
                            v-for="task in tasks"
                            :key="task.id"
                            class="task-item mb-2 p-2 border rounded d-flex align-items-center"
                        >
                            <input
                                class="mr-2"
                                type="checkbox"
                                :id="'task-' + task.id"
                                :value="task.id"
                                v-model="selectedTasks"
                                :disabled="!hasInternalId(task)"
                            >
                            <label class="flex-grow-1 mb-0" :for="'task-' + task.id" style="cursor: pointer;">
                                <span class="font-weight-bold">{{ task.title }}</span>
                                <span v-if="task.project_name" class="text-muted"> | {{ task.project_name }}</span>
                                <small v-if="!hasInternalId(task)" class="text-danger font-italic ml-2">
                                    {{ trans('Tasks need Internal ID (Task or Project)') }}
                                </small>
                            </label>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import VueFlatpickr from 'vue-flatpickr-component';
import { LocalDate } from '@js-joda/core';

export default {
    components: {
        VueFlatpickr,
    },
    data() {
        return {
            filter: {
                date_start: '',
                date_end: '',
                selected_departments: [],
            },
            departments: [],
            tasks: [],
            selectedTasks: [],
            loadingDepartments: false,
            loadingTasks: false,
            loadingExport: false,
            hasFiltered: false,
            validationErrors: null,
        };
    },
    computed: {
        areAllDepartmentsSelected() {
            return this.departments.length > 0 && 
                   this.filter.selected_departments.length === this.departments.length;
        },
        selectedDepartmentsText() {
            if (this.filter.selected_departments.length === 0) {
                return trans('No departments selected (all will be included)');
            } else if (this.filter.selected_departments.length === this.departments.length) {
                return trans('All departments selected');
            } else {
                return trans(':count departments selected', { count: this.filter.selected_departments.length });
            }
        },
    },
    mounted() {
        this.initializeDates();
        this.fetchDepartments();
    },
    methods: {
        initializeDates() {
            // Set start date to first day of previous month
            const now = LocalDate.now();
            const previousMonth = now.minusMonths(1);
            const startOfPreviousMonth = previousMonth.withDayOfMonth(1);
            const endOfPreviousMonth = previousMonth.withDayOfMonth(previousMonth.lengthOfMonth());

            this.filter.date_start = startOfPreviousMonth.toString();
            this.filter.date_end = endOfPreviousMonth.toString();
        },
        onDateStartChange(value) {
            this.filter.date_start = value;
            
            // Validate date range: if start date is after end date, adjust end date
            if (value && this.filter.date_end) {
                const startDate = LocalDate.parse(value);
                const endDate = LocalDate.parse(this.filter.date_end);
                
                if (startDate.isAfter(endDate)) {
                    // Set end date to start date + 1 day
                    this.filter.date_end = startDate.plusDays(1).toString();
                }
            }
        },
        onDateEndChange(value) {
            this.filter.date_end = value;
            
            // Validate date range: if end date is before start date, adjust start date
            if (value && this.filter.date_start) {
                const startDate = LocalDate.parse(this.filter.date_start);
                const endDate = LocalDate.parse(value);
                
                if (endDate.isBefore(startDate)) {
                    // Set start date to end date - 1 day
                    this.filter.date_start = endDate.minusDays(1).toString();
                }
            }
        },
        toggleAllDepartments() {
            if (this.areAllDepartmentsSelected) {
                this.filter.selected_departments = [];
            } else {
                this.filter.selected_departments = this.departments.map(dept => dept.id);
            }
        },
        async fetchDepartments() {
            this.loadingDepartments = true;
            try {
                const response = await axios.get(route('export-sap.departments'));
                this.departments = response.data;
            } catch (error) {
                console.error('Error fetching departments:', error);
            } finally {
                this.loadingDepartments = false;
            }
        },
        async filterTasks() {
            this.loadingTasks = true;
            this.hasFiltered = true;
            this.selectedTasks = [];
            this.validationErrors = null; // Clear validation errors when filtering
            
            try {
                const response = await axios.post(route('export-sap.filter'), {
                    date_start: this.filter.date_start,
                    date_end: this.filter.date_end,
                    department_ids: this.filter.selected_departments,
                });
                this.tasks = response.data;
            } catch (error) {
                console.error('Error filtering tasks:', error);
                this.tasks = [];
            } finally {
                this.loadingTasks = false;
            }
        },
        hasInternalId(task) {
            return !!(task.internal_id || (task.project && task.project.internal_id));
        },
        selectAll() {
            this.selectedTasks = this.tasks
                .filter(task => this.hasInternalId(task))
                .map(task => task.id);
        },
        clearAll() {
            this.selectedTasks = [];
        },
        async exportToExcel() {
            if (this.selectedTasks.length === 0) {
                return;
            }

            this.loadingExport = true;
            this.validationErrors = null; // Clear previous validation errors
            
            try {
                // First make request without blob responseType to handle potential validation errors
                const response = await axios.post(route('export-sap.export'), {
                    date_start: this.filter.date_start,
                    date_end: this.filter.date_end,
                    task_ids: this.selectedTasks,
                });

                // If we get here without error, it means validation passed
                // Now make the actual blob request for the file download
                const fileResponse = await axios.post(route('export-sap.export'), {
                    date_start: this.filter.date_start,
                    date_end: this.filter.date_end,
                    task_ids: this.selectedTasks,
                }, {
                    responseType: 'blob'
                });

                // Create download link
                const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
                const link = document.createElement('a');
                link.href = url;
                
                // Extract filename from response headers or use default
                const contentDisposition = fileResponse.headers['content-disposition'];
                let filename = 'SAP Export.xlsx';
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                    if (filenameMatch) {
                        filename = filenameMatch[1];
                    }
                }
                
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error exporting to Excel:', error);
                
                // Handle validation errors
                if (error.response && error.response.status === 422 && error.response.data.errors) {
                    this.validationErrors = error.response.data.errors;
                    
                    // Scroll to validation errors
                    this.$nextTick(() => {
                        const errorElement = document.querySelector('.card .bg-danger');
                        if (errorElement) {
                            errorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    });
                }
            } finally {
                this.loadingExport = false;
            }
        },
    },
};
</script>

<style scoped>
.task-item:hover {
    background-color: #f8f9fa;
}

.task-item.selected {
    background-color: #e3f2fd;
}

.departments-container .form-check-label {
    cursor: pointer;
    user-select: none;
}
</style> 